import type { SupSingleVaultV3, SupVaultReward, SupWeb3Address, SupWeb3Asset } from "@/types";
import React, { type CSSProperties, type MouseEventHandler, useCallback, useMemo } from "react";

import { POINTS_AMOUNT_REWARD } from "@/components/Ui/Label/Rewards/constant";
import { isSpecialPointRewardByProtocol } from "@/components/Ui/Label/Rewards/utils";

import { useRouter } from "next/navigation";
import Link from "next/link";

import useApeIn from "@/hooks/tx-flow/buttons/useApeIn";
import useDepositButton from "@/hooks/tx-flow/buttons/useDepositButton";
import { useStoredDataDispatch } from "@/store";
import { SupLoader } from "@repo/core-ui";

import ChipVaultAssets from "@/components/Ui/Chip/Vault/Assets";
import UiLabelRewardsTag from "@/components/Ui/Label/Rewards/Tag";
import { Attribute } from "@/components/Explore/Attribute";
import { QuickDepositButton } from "./QuickDepositButton";
import { DepositButton } from "@/components/Explore/Vaults/DepositButton";
import { UpTrend } from "@/components/Explore/UpTrend";
import { DownTrend } from "@/components/Explore/DownTrend";
import { SupPoolLogo } from "@/components/Explore/Vaults/SupPoolLogo";
import { formatCurrencyCompact, notifyDepositChange } from "@/utils";
import UiLabelApy from "@/components/Ui/Label/Apy";
import UiTooltipUnsupported from "@/components/Ui/Tooltip/Unsupported";
import SupPoint from "@/utils/icons/Point";

interface VaultCardProps {
  data: {
    id: string;
    name: string;
    inflows?: number;
    apy: number;
    tvl: number;
    hasSuperPool: boolean;
    superform_address: SupWeb3Address | null;
    protocol?: {
      id: string;
      logo: string;
      name: string;
    };
    token: {
      logo: string;
      name: string;
    };
    chain: {
      id: number;
      logo: string;
      name: string;
    };
    rewards: SupVaultReward[];
    assets: SupWeb3Asset[];
  };
  className?: string;
  style?: CSSProperties;
  skipApyMultiplier?: boolean;
}

export default function VaultCard({ data, skipApyMultiplier = false, ...props }: VaultCardProps) {
  const { push } = useRouter();
  const { id, name, apy, superform_address, inflows, tvl, assets, rewards, chain, protocol, hasSuperPool } = data;

  const hasRewards = rewards.length > 0;

  const storedDispatch = useStoredDataDispatch();

  const singleVault = useMemo(
    () =>
      ({
        id,
        chain,
        slug: id,
        availability: "available",
        superform_address: superform_address,
        vault_statistics: {
          displayApy: skipApyMultiplier ? apy : apy * 100,
        },
        protocol: protocol,
        rewards: rewards ?? [],
      }) as unknown as SupSingleVaultV3,
    [id, chain, superform_address, skipApyMultiplier, apy, protocol, rewards]
  );

  const { reason, isDisabled, isCompleted, isLoading } = useDepositButton(singleVault);
  const handleApeIn = useApeIn(singleVault);

  const onClickDeposit = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation();
      storedDispatch({ vault: singleVault, type: "toggleCart" });
      notifyDepositChange(singleVault, storedDispatch);
    },
    [singleVault, storedDispatch]
  );

  const onClickDepositRemove: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    storedDispatch({ vault: singleVault, type: "toggleCart" });
  };

  const onClickBuyNow = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation();
      handleApeIn();
    },
    [handleApeIn]
  );

  return (
    <div
      onClick={() => push(`/vault/${id}/`)}
      className="!block cursor-pointer rounded-l-2xl rounded-br-2xl border-0.5 border-primary-300 bg-neutral-25 hover:border-primary-500 hover:bg-gradient-to-r hover:from-primary-25/20 hover:from-10% hover:to-primary-100/20 hover:to-[145%] hover:text-primary-950"
      {...props}
    >
      {/** Header */}
      <div className="relative">
        <div className="-ml-[0.5px] -mt-[0.5px] flex w-fit items-center gap-1 rounded-br-2xl rounded-tl-2xl border-0.5 border-primary-500 bg-primary-25 py-3 pl-4 pr-2 text-2xl font-bold leading-6 tracking-[0.5px] text-primary-700">
          {hasRewards ? (
            <UiLabelRewardsTag useFallback isFlexHeight unwrapped iconClass="h-6 w-6 order-last" vault={singleVault} noAction />
          ) : (
            <UiLabelApy vault={singleVault} unwrapped noAction useFallback />
          )}
        </div>

        <div className="absolute right-3 top-2 text-right">
          {inflows !== undefined && inflows !== 0 && (
            <div className="mb-1 flex items-center gap-1 text-xs uppercase leading-4 text-neutral-800">
              {inflows >= 0 ? <UpTrend /> : <DownTrend />}
              {formatCurrencyCompact(inflows, 2)}
              <span className="text-neutral-500">Inflows</span>
            </div>
          )}
          <div className="text-xs leading-4 text-neutral-800">
            {formatCurrencyCompact(tvl, 2)} <span className="text-neutral-500">TVL</span>
          </div>
        </div>
      </div>

      {/** Body */}
      <div className="flex h-[10.125rem] items-center gap-4 px-3">
        <ChipVaultAssets assets={assets} vaultId={id} />

        <div className="block">
          <div className="mb-0.5 font-sans font-medium leading-6 tracking-[0.5px] text-neutral-900">
            <Link href={`/vault/${id}/`} className="hover:text-primary-800">
              {name}
            </Link>
          </div>
          <div className="flex items-center gap-1">
            {chain.id ? <Attribute useWhiteBg logo={chain.logo} label={chain.name} /> : null}
            {hasSuperPool && <Attribute logo={<SupPoolLogo />} label="Superpool" />}
          </div>
        </div>
      </div>

      {/** Footer */}
      <div className="flex h-10 items-center justify-between border-t-0.5 border-primary-300 pl-3">
        {protocol && (
          <div className="flex items-center gap-2">
            <Link onClick={e => e.stopPropagation()} href={`/protocol/${protocol.id}/`} className="group inline-flex">
              <Attribute logo={protocol.logo} label={protocol.name} />
            </Link>
            <Attribute
              logo={<SupPoint className="h-4 w-auto" />}
              label={`${isSpecialPointRewardByProtocol(protocol) ? POINTS_AMOUNT_REWARD.special : POINTS_AMOUNT_REWARD.regular}x points`}
            />
          </div>
        )}

        {isLoading ? (
          <div className="ml-auto flex w-[116px] items-center justify-center">
            <SupLoader isSmall />
          </div>
        ) : (
          <UiTooltipUnsupported reason={reason} arrowPlacement="top" enable={isDisabled}>
            <div className="ml-auto flex items-center">
              <QuickDepositButton disabled={isDisabled} onAdd={onClickDeposit} onRemove={onClickDepositRemove} selected={isCompleted} className="-mt-[0.5px]" />
              <DepositButton disabled={isCompleted || isDisabled} className="-mr-[0.5px] -mt-[0.5px] border-l-0" handleClick={onClickBuyNow} />
            </div>
          </UiTooltipUnsupported>
        )}
      </div>
    </div>
  );
}
